import { motion, useReducedMotion } from 'framer-motion'

const EASE = [0.16, 1, 0.3, 1]

/**
 * "My Cards" for a visitor who is not logged in: a short invitation set against the wall display,
 * which shows what the account is for (a collection you can look at and rearrange).
 *
 * The photo is shot on a mid-grey wall, so it is dimmed and melted into the page colour; only the
 * lilac backlight and the glowing slot keep their colour. Text on the left, picture on the right
 * on desktop; picture on top on phones.
 */
export default function OwnerSignedOut({ eyebrow, title, subtitle, note, actions }) {
  const reduceMotion = useReducedMotion()

  const rise = (delay) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.9, ease: EASE, delay },
        }

  return (
    <section className="relative isolate overflow-hidden pt-[calc(56px+env(safe-area-inset-top,0px))] lg:flex lg:min-h-[min(84svh,760px)] lg:items-center">
      <div className="relative aspect-[16/10] w-full overflow-hidden lg:absolute lg:inset-y-0 lg:right-0 lg:aspect-auto lg:w-[66%]">
        <motion.img
          src="/home/owner-wall.jpg"
          width="1024"
          height="576"
          alt="A wall display holding graded cards in a row, with one card sliding out of its slot under a lilac backlight"
          fetchPriority="high"
          decoding="async"
          draggable="false"
          className="m1nt-owner-wall absolute inset-0 block h-full w-full select-none object-cover"
          {...(reduceMotion
            ? {}
            : {
                initial: { opacity: 0, scale: 1.08 },
                animate: { opacity: 1, scale: 1 },
                transition: { duration: 2, ease: EASE },
              })}
        />
        <div className="m1nt-grain pointer-events-none absolute inset-0" aria-hidden="true" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, #0A090D 0%, rgba(10,9,13,0) 20%), linear-gradient(to top, #0A090D 0%, rgba(10,9,13,0) 38%)',
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 hidden lg:block"
          style={{
            background:
              'linear-gradient(to right, #0A090D 0%, rgba(10,9,13,0.9) 16%, rgba(10,9,13,0) 58%)',
          }}
          aria-hidden="true"
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-[var(--gutter)] pb-[var(--section)] pt-4 lg:py-[var(--section)]">
        <div className="max-w-[520px]">
          <motion.p className="text-[14px] text-m1-muted" {...rise(0.2)}>
            {eyebrow}
          </motion.p>
          <motion.h1
            className="mt-4 font-m1-display text-[clamp(36px,4.6vw,58px)] font-medium leading-[1.04] tracking-[-0.015em] text-m1-ink sm:font-normal"
            {...rise(0.32)}
          >
            {title}
          </motion.h1>
          <motion.p
            className="mt-6 max-w-[30em] text-[19px] font-light leading-relaxed text-m1-ink-soft"
            {...rise(0.44)}
          >
            {subtitle}
          </motion.p>
          <motion.div className="mt-8" {...rise(0.56)}>
            {actions}
          </motion.div>
          <motion.p
            className="mt-10 max-w-[30em] text-[16px] font-light leading-relaxed text-m1-muted"
            {...rise(0.68)}
          >
            {note}
          </motion.p>
        </div>
      </div>
    </section>
  )
}
